import { useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Icon, IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { isIamV2OidcEnabled, isSyncUserGroupsAndOrgsFromOIDC, } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useCreateOidcConnectionMutation, useCreateSamlConnectionMutation, useUpdateAttributeMappingMutation, useUpdateOIDCConnecitonMutation, useUpdateSamlConnectionMutation, } from '../../../services/saml/saml-service';
import { defaultFormValues, getDefaultSamlHeaders, getSubDomainIfOrgEnabled, IDP_TYPE, mapAttributeFields, TranslationsKeys, } from '../constant';
import styles from './attribute-mapping.module.scss';
export const AttributeMapping = ({ onProviderForm, setScreenLoader, clientId, backToIdentityDetails, formDataRef, edit, showHideBanner, resetEdit, attributeValues, currentOrgInfo, }) => {
    const { t } = useTranslation();
    const getTranslations = (key, args) => t(`${key}`, args);
    const [attributeMap, setAttributeMap] = useState({});
    const [continueClick, setContinueClick] = useState(false);
    const [allowToContinue, setAllowToContinue] = useState(true);
    const [samlData, setSamlData] = useState({});
    const [numberOfRetry, setNumberOfRetry] = useState(1);
    const attributeFieldsRef = useRef(mapAttributeFields);
    const backButton = useRef(false);
    const syncUserGroupsAndOrgsFromOIDC = isSyncUserGroupsAndOrgsFromOIDC();
    const isSaml = formDataRef.current.type === IDP_TYPE.SAML;
    const resetFieldStatus = () => {
        attributeFieldsRef.current.map(({ fieldStatus }, index) => {
            if (index > 1) {
                fieldStatus = false;
            }
        });
        const ssoEdited = {
            url: '',
            binding: 'POST',
        };
        const mapDefult = {
            username: 'subjectNameId',
            email: !isSaml ? 'email' : '',
            displayName: '',
            locale: '',
            timezone: '',
            roles: '',
            orgs: '',
            defaultOrg: '',
            groupsWithOrg: '',
        };
        formDataRef.current.name = '';
        formDataRef.current.issuer = '';
        formDataRef.current.sso = ssoEdited;
        formDataRef.current.certData = { x5c: '' };
        formDataRef.current.mappings = mapDefult;
        formDataRef.current = {
            ...formDataRef.current,
            connectionName: '',
            scopes: 'profile email openid',
            claims: '',
            IDPClientId: '',
            clientSecret: '',
            authorizationEndpoint: '',
            tokenEndpoint: '',
            userinfoEndpoint: '',
            jwksEndpoint: '',
            autoCreateUser: true,
        };
    };
    const onMappingSuccess = (response) => {
        setScreenLoader(false);
        setNumberOfRetry(0);
        resetFieldStatus();
        onProviderForm(false, true, false);
    };
    const [updateAttributeMapping] = useUpdateAttributeMappingMutation(response => {
        onMappingSuccess(response);
    }, (error) => {
        if (numberOfRetry > 0) {
            setNumberOfRetry(numberOfRetry - 1);
            updateAttributeMapping();
        }
        else if (!edit) {
            setContinueClick(false);
            showHideBanner(getTranslations(TranslationsKeys.MAP_ATTRIBUTE_ERROR), true);
            onProviderForm(true, false, false);
        }
        else {
            setContinueClick(false);
            setScreenLoader(false);
            showHideBanner(getTranslations(TranslationsKeys.UPDATE_CONNECTION_ERROR), true);
        }
    }, {
        id: formDataRef.current.id,
        isSaml: formDataRef.current.type === IDP_TYPE.SAML,
        clientId,
        ...attributeMap,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const onSamlConnectionSuccess = (response) => {
        const { id } = response?.Data;
        formDataRef.current.id = id;
        setContinueClick(false);
        updateAttributeMapping();
    };
    const onSamlConnectionError = (errors) => {
        const errorMessage = errors?.networkError?.result
            ?.errors?.[0]?.message;
        let message = edit
            ? TranslationsKeys.UPDATE_CONNECTION_ERROR
            : TranslationsKeys.CREATE_CONNECTION_ERROR;
        if (!edit && errorMessage?.includes('409')) {
            message = TranslationsKeys.DUPLICATE_NAME_ERROR;
        }
        setContinueClick(false);
        setScreenLoader(false);
        showHideBanner(getTranslations(message), true);
    };
    const [createSamlConnection] = useCreateSamlConnectionMutation(response => onSamlConnectionSuccess(response), errors => onSamlConnectionError(errors), { ...samlData, clientId, defaultHeaders: getDefaultSamlHeaders() });
    const onOidcConnectionError = (errors) => {
        const errorMessage = errors?.networkError?.result
            ?.errors?.[0]?.message;
        let message = edit
            ? TranslationsKeys.OIDC_UPDATE_CONNECTION_ERROR
            : TranslationsKeys.OIDC_CREATE_CONNECTION_ERROR;
        if (!edit && errorMessage?.includes('409')) {
            message = TranslationsKeys.OIDC_DUPLICATE_NAME_ERROR;
        }
        setScreenLoader(false);
        setContinueClick(false);
        showHideBanner(getTranslations(message), true);
    };
    const onOidcConnectionSuccess = (response) => {
        const { id } = response?.Data;
        formDataRef.current.id = id;
        setContinueClick(false);
        updateAttributeMapping();
    };
    const [createOidcConnection] = useCreateOidcConnectionMutation(response => onOidcConnectionSuccess(response), errors => onOidcConnectionError(errors), {
        ...samlData,
        clientId,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const [updateOidcConnection] = useUpdateOIDCConnecitonMutation(response => onOidcConnectionSuccess(response), errors => onOidcConnectionError(errors), {
        id: formDataRef.current.id,
        ...samlData,
        clientId,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const [updateSamlConnection] = useUpdateSamlConnectionMutation(response => onSamlConnectionSuccess(response), errors => onSamlConnectionError(errors), {
        id: formDataRef.current.id,
        ...samlData,
        clientId,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const updateOidcData = () => {
        const { connectionName, scopes, claims, IDPClientId, clientSecret, authorizationEndpoint, tokenEndpoint, issuer, userinfoEndpoint, jwksEndpoint, autoCreateUser, type, } = formDataRef.current;
        let subDomain = getSubDomainIfOrgEnabled();
        if (!subDomain && currentOrgInfo) {
            subDomain = `${currentOrgInfo.orgName}_`;
        }
        let nameWithOrg = connectionName;
        if (subDomain && !connectionName.startsWith(subDomain)) {
            nameWithOrg = `${subDomain}${connectionName}`;
        }
        setSamlData({
            connectionName: nameWithOrg,
            scopes,
            claims,
            IDPClientId,
            clientSecret,
            authorizationEndpoint,
            tokenEndpoint,
            issuer,
            userinfoEndpoint,
            jwksEndpoint,
            autoCreateUser,
            type,
        });
    };
    const updateSamlData = () => {
        const { name, sso, issuer, certData, requestSignatureAlgorithm, responseSignatureAlgorithm, maxClockSkew, ...rest } = formDataRef.current;
        let subDomain = getSubDomainIfOrgEnabled();
        if (!subDomain && currentOrgInfo) {
            subDomain = `${currentOrgInfo.orgName}_`;
        }
        let nameWithOrg = name;
        if (subDomain && !name.startsWith(subDomain)) {
            nameWithOrg = `${subDomain}${name}`;
        }
        setSamlData({
            name: nameWithOrg,
            type: 'SAML2',
            status: 'ACTIVE',
            sso: {
                url: sso.url,
                binding: sso.binding,
            },
            issuer,
            certData: {
                x5c: certData.x5c,
            },
            requestSignatureAlgorithm,
            responseSignatureAlgorithm,
            maxClockSkew,
        });
    };
    useEffect(() => {
        if (Object.keys(samlData).length > 0) {
            if (edit) {
                if (formDataRef.current.type === IDP_TYPE.SAML)
                    updateSamlConnection();
                else
                    updateOidcConnection();
            }
            else if (formDataRef.current.type === IDP_TYPE.SAML) {
                createSamlConnection();
            }
            else {
                createOidcConnection();
            }
        }
    }, [samlData]);
    const checkForContinue = () => {
        let status = false;
        attributeFieldsRef.current.map(({ id, fieldStatus, mandatory }) => {
            if (mandatory && !fieldStatus) {
                if (!(isSaml && id === 'email')) {
                    status = true;
                }
            }
        });
        setAllowToContinue(status);
    };
    const setFieldStatus = (updatedAttributeMap) => {
        let status = false;
        attributeFieldsRef.current.map(({ id, fieldStatus, mandatory }, index) => {
            if (!updatedAttributeMap[id]) {
                status = false;
            }
            else {
                status = true;
            }
            attributeFieldsRef.current[index].fieldStatus = status;
        });
        checkForContinue();
    };
    const setAttributeField = (attMap) => {
        const { email, username, displayName, locale, timezone, roles, orgs, defaultOrg, groupsWithOrg, } = attMap;
        setScreenLoader(false);
        const latestAttributeMap = {
            username,
            email: email || '',
            displayName: displayName || '',
            locale: locale || '',
            timezone: timezone || '',
            roles: roles || '',
            orgs: orgs || '',
            defaultOrg: defaultOrg || '',
            groupsWithOrg: groupsWithOrg || '',
        };
        setAttributeMap(latestAttributeMap);
        setFieldStatus(latestAttributeMap);
    };
    useEffect(() => {
        if (edit) {
            if (!_.isEmpty(attributeValues.current) && !backButton.current) {
                setAttributeField(attributeValues.current);
            }
            else {
                setAttributeField(formDataRef.current.mappings);
            }
        }
        else {
            if (formDataRef.current.type !== IDP_TYPE.SAML) {
                const emailMapping = formDataRef.current.mappings.email;
                formDataRef.current.mappings = {
                    ...formDataRef.current.mappings,
                    username: emailMapping,
                };
            }
            setAttributeField(formDataRef.current.mappings);
        }
        backButton.current = false;
    }, []);
    useEffect(() => {
        if (continueClick) {
            if (formDataRef.current.type !== IDP_TYPE.SAML)
                updateOidcData();
            else
                updateSamlData();
        }
    }, [attributeMap]);
    const onFieldChange = ({ target }, id) => {
        const updateAttribute = { ...attributeMap };
        updateAttribute[id] = target.value;
        setFieldStatus(updateAttribute);
        setAttributeMap(updateAttribute);
    };
    const getSamlAttribute = (fieldName) => {
        return React.createElement("div", { className: styles.samlAttributeField }, fieldName);
    };
    const getArrowWithStatus = (fieldStatus) => {
        return (React.createElement(Vertical, { className: styles.spaceInFeilds, vAlignContent: "center" },
            React.createElement(Icon, { id: IconID.ARROW_RIGHT, size: IconSize.LARGE, color: fieldStatus ? IconColor.BLACK : IconColor.GRAY })));
    };
    const getThoughtspotAttribute = (fieldName, id) => {
        return (React.createElement(Input.Text, { ariaLabel: fieldName, "data-testid": id, value: attributeMap ? attributeMap[id] : '', onChange: event => onFieldChange(event, id), className: styles.spaceInFeilds }));
    };
    const renderFields = () => {
        let fieldNameString;
        return attributeFieldsRef.current.map(({ id, fieldName, fieldStatus }, index) => {
            fieldNameString = getTranslations(fieldName);
            if (isSaml && id === 'email') {
                fieldNameString = fieldNameString.split('*')[0].trim();
            }
            if (!syncUserGroupsAndOrgsFromOIDC && id === 'orgs') {
                return '';
            }
            if (!syncUserGroupsAndOrgsFromOIDC && id === 'defaultOrg') {
                return '';
            }
            return (React.createElement(Horizontal, { key: id },
                getSamlAttribute(fieldNameString),
                getArrowWithStatus(fieldStatus),
                getThoughtspotAttribute(fieldNameString, id)));
        });
    };
    const onContinue = () => {
        const updateAttribute = { ...attributeMap };
        attributeFieldsRef.current.map(({ id, fieldStatus }) => {
            if (!fieldStatus) {
                updateAttribute[id] = '';
            }
        });
        setContinueClick(true);
        setAttributeMap(updateAttribute);
        setScreenLoader(true);
    };
    const onCancel = () => {
        setNumberOfRetry(0);
        formDataRef.current = defaultFormValues;
        resetEdit();
        resetFieldStatus();
        onProviderForm(true, false, false);
    };
    const onBack = () => {
        setNumberOfRetry(0);
        formDataRef.current.mappings = attributeMap;
        backButton.current = true;
        backToIdentityDetails();
    };
    return (React.createElement(Vertical, { spacing: "h", className: styles.paddingTop32 },
        React.createElement(Vertical, null,
            React.createElement(Typography, { variant: "h3", noMargin: true }, getTranslations(TranslationsKeys.MAP_HEADER)),
            React.createElement(Typography, { variant: "p-large" }, isSaml
                ? getTranslations(TranslationsKeys.MAP_SUBHEADER)
                : getTranslations(TranslationsKeys.MAP_SUBHEADER_OIDC)),
            isSaml && (React.createElement(Typography, { variant: "p-large" }, getTranslations(TranslationsKeys.MAP_SUBHEADER2))),
            isSaml && (React.createElement(Typography, { variant: "p-large", noMargin: true }, getTranslations(TranslationsKeys.MAP_SUBHEADER3)))),
        React.createElement(Vertical, { spacing: "e" },
            React.createElement(Horizontal, { spacing: "j" },
                React.createElement(Vertical, null,
                    React.createElement(Typography, { variant: "p-large-bold", noMargin: true }, getTranslations(TranslationsKeys.MAP_COLUMN2))),
                React.createElement(Vertical, null,
                    React.createElement(Typography, { variant: "p-large-bold", noMargin: true, className: styles.spaceInHeader }, isIamV2OidcEnabled()
                        ? getTranslations(TranslationsKeys.OIDC_MAP_COLUMN1)
                        : getTranslations(TranslationsKeys.MAP_COLUMN1)))),
            renderFields()),
        React.createElement(Horizontal, { spacing: "e", hAlignContent: "left" },
            React.createElement(Vertical, null,
                React.createElement(Button, { text: getTranslations(TranslationsKeys.SAVE_CONTINUE), onClick: onContinue, isDisabled: allowToContinue, "data-testid": "continueFromMappings" })),
            React.createElement(Vertical, null,
                React.createElement(Button, { type: "secondary", text: getTranslations(TranslationsKeys.BACK), onClick: onBack })),
            React.createElement(Vertical, null,
                React.createElement(Button, { "data-testid": "mappingsCancelButton", type: "secondary", text: getTranslations(TranslationsKeys.CANCEL), onClick: onCancel })))));
};
//# sourceMappingURL=attribute-mapping.container.js.map